import * as React from 'react';

import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import axios from '../../common/axios-instance/axios-instance';
import { AxiosResponse, AxiosError } from 'axios';

import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';

import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';

import { ILicense, ILicensePagingResponse } from '../../@types';
import { IStore } from '../../modules/root-reducer';
import { IColumnData } from '../../common/responsive-table/responsive-table-head';
import { displayDate } from '../../common/utils';
import NumberFormat from 'react-number-format';

interface ITableState {
  page: number;
  totalElements: number;
  filterText: string;
  rows: ILicense[];
  licenseKey: string;
  snackbarOpen: boolean;
}

interface ITableProps {
  customerId: number;
}

const styles = createStyles({
  root: {
    overflowX: 'auto'
  },
  rowHover: {
    cursor: 'pointer'
  },
  copyColumn: {
    width: '13%'
  },
  hidden: {
    height: 0,
    margin: 0,
    padding: 0,
    border: 0
  }
});

const headers: IColumnData[] = [
  { id: 'appName', label: 'App Name' },
  { id: 'os', label: 'OS' },
  { id: 'cost', label: 'Cost' },
  { id: 'expirationDate', label: 'Expiration Date' },
  { id: 'status', label: 'Status' },
  { id: 'copyLicense', label: 'License Key' }
];

class LicenseTable extends React.Component<
  RouteComponentProps<any> & ITableProps & WithStyles<typeof styles>,
  ITableState
> {
  readonly state: ITableState = {
    page: 0,
    totalElements: 0,
    filterText: '',
    rows: [],
    licenseKey: '',
    snackbarOpen: false
  };

  private licenseKeyRef: React.RefObject<any> = React.createRef();

  componentDidUpdate = (prevProps: ITableProps) => {
    if (
      this.props.customerId &&
      this.props.customerId !== prevProps.customerId
    ) {
      this.updateTable();
    }
  };

  handleCopyLicense = (licenseKey: string) => {
    this.setState({ licenseKey, snackbarOpen: true }, this.copyLicense);
  };

  copyLicense = () => {
    if (this.licenseKeyRef.current) {
      this.licenseKeyRef.current.select();
      document.execCommand('copy');
    }
  };

  handleCloseSnackbar = () => {
    this.setState({ snackbarOpen: false });
  };

  handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement>,
    page: number
  ) => {
    this.setState({ page }, this.updateTable);
  };

  updateTable() {
    const { page } = this.state;
    const { customerId } = this.props;
    const serverPage = page + 1;

    const url =
      '/api/license/page?page=' +
      serverPage +
      '&size=10' +
      '&orderBy=expirationDate&order=asc&customerId=' +
      customerId;

    return axios
      .get<ILicensePagingResponse>(url)
      .then((response: AxiosResponse<ILicensePagingResponse>) => {
        const content = response.data.content;
        this.setState({
          rows: content,
          totalElements: response.data.totalElements
        });
      })
      .catch((error: AxiosError) => {
        console.log(error.message);
      });
  }

  render() {
    const { rows, page, totalElements } = this.state;

    const { classes, customerId, history } = this.props;

    const emptyRows = 10 - rows.length;

    let display;
    if (customerId) {
      if (rows.length > 0) {
        display = 'showLicenses';
      } else {
        display = 'noLicenses';
      }
    } else {
      display = 'noCustomer';
    }

    return (
      <Card className={classes.root}>
        <CardHeader
          title="Licenses"
          action={
            <Tooltip title="Add License">
              <span>
                <IconButton
                  disabled={!customerId}
                  // tslint:disable-next-line jsx-no-lambda
                  onClick={() =>
                    this.props.history.push('/licenses/new/' + customerId)
                  }
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          }
        />
        <CardContent>
          {
            {
              noCustomer: (
                <Typography variant="body2">
                  You must create the customer before adding licenses
                </Typography>
              ),
              noLicenses: (
                <Typography variant="body2">
                  This customer currently has no licenses
                </Typography>
              ),
              showLicenses: (
                <div>
                  <input
                    className={classes.hidden}
                    value={this.state.licenseKey}
                    ref={this.licenseKeyRef}
                    readOnly
                  />
                  <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    open={this.state.snackbarOpen}
                    onClose={this.handleCloseSnackbar}
                    ContentProps={{
                      'aria-describedby': 'message-id'
                    }}
                    message={<span id="message-id">License key copied!</span>}
                    autoHideDuration={2000}
                  />
                  <Table>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableCell key={header.id}>{header.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(license => (
                        <TableRow
                          hover
                          key={license.id}
                          className={classes.rowHover}
                          // tslint:disable-next-line jsx-no-lambda
                          onClick={() =>
                            history.push('/licenses/view/' + license.id)
                          }
                        >
                          <TableCell>{license.appName}</TableCell>
                          <TableCell>{license.os}</TableCell>
                          <TableCell>
                            <NumberFormat
                              thousandSeparator
                              prefix="$"
                              value={license.costInitial}
                              displayType="text"
                            />
                          </TableCell>
                          <TableCell>
                            {displayDate(new Date(license.expirationDate))}
                          </TableCell>
                          <TableCell>{license.status}</TableCell>
                          <TableCell className={classes.copyColumn}>
                            <Tooltip title="Copy license key">
                              <IconButton
                                // tslint:disable-next-line jsx-no-lambda
                                onClick={e => {
                                  e.stopPropagation();
                                  this.handleCopyLicense(license.licenseKey);
                                }}
                              >
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {totalElements > 10 && emptyRows > 0 && (
                        <TableRow style={{ height: 48 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  {totalElements > 10 && (
                    <TablePagination
                      component="div"
                      count={totalElements}
                      rowsPerPage={10}
                      page={page}
                      rowsPerPageOptions={[10]}
                      onChangePage={this.handleChangePage}
                    />
                  )}
                </div>
              )
            }[display]
          }
        </CardContent>
      </Card>
    );
  }
}

const mapStateToProps = (state: IStore) => ({
  customerId: state.customerReducer.customer.id
});

const enhance = compose(
  connect(mapStateToProps),
  withStyles(styles),
  withRouter
);

export default enhance(LicenseTable);



// WEBPACK FOOTER //
// ./src/features/license-table/license-table.tsx